/**
 * SessionLive — one app-wide, reconnecting WebSocket that is BOUND TO THE
 * CAMPAIGN SESSION so session-scoped broadcasts (`session/log-entry`,
 * `session/status`) reach the client from any page.
 *
 * Why another socket? The per-page CampaignSocket (Sheet, Dashboard, Party)
 * and the VTT's SceneClient each subscribe to the session, but they live and
 * die with their page: the DM on the battle map, or a player on the lore
 * page, has no connection row with a SessionId — and the roll broadcast fans
 * out over the BySession GSI, so those clients never hear a roll. The roll
 * feed (dev handoff 2026-08-24, `vtt-dm-roll-visibility`) needs to work from
 * every view, so it needs a connection that outlives the page.
 *
 * WorldMapLive is the closest sibling, but it deliberately never sends
 * anything (world/* events don't need a session binding). This client:
 *
 *   - sends `scene/subscribe { sessionId, mapId }` on every (re)open so the
 *     connection row's SessionId is populated — that's what routes session
 *     broadcasts to us. The resulting `scene/state` snapshot is ignored.
 *   - exposes `send()` so the feed can post an off-app roll through the
 *     same `session/roll` path the sheet uses (audit + broadcast).
 *   - dispatches every envelope type to registered listeners; consumers
 *     filter by type. Listeners survive reconnects.
 *   - reconnects with capped exponential backoff + jitter, and re-subscribes.
 *
 * One socket per tab. Mount once from the app shell (RollFeed does this).
 */
import { openSocket } from './socket';
import { SceneActions } from '../vtt/net/sceneWire';
import type { ServerEnvelope } from './runtimeProtocol';

export type SessionLiveStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'stopped';

type Listener = (payload: unknown, envelope: ServerEnvelope<unknown>) => void;
type StatusListener = (status: SessionLiveStatus) => void;

export interface SessionLiveOptions {
  sessionId: string;
  /** Map id for the subscribe handshake — any map the viewer may see. */
  mapId: string;
  maxBackoffMs?: number;
}

export class SessionLive {
  private ws: WebSocket | null = null;
  private stopped = true;
  private running = false;
  private backoffMs = 500;
  private readonly maxBackoffMs: number;
  private readonly listeners = new Map<string, Set<Listener>>();
  private readonly statusListeners = new Set<StatusListener>();
  private _status: SessionLiveStatus = 'idle';
  private pendingSends: string[] = [];
  private everOpened = false;

  constructor(private readonly opts: SessionLiveOptions) {
    this.maxBackoffMs = opts.maxBackoffMs ?? 15_000;
  }

  get status(): SessionLiveStatus {
    return this._status;
  }

  /** Register a listener for a server envelope type. Returns an unsubscribe fn. */
  on<T = unknown>(type: string, fn: (payload: T, envelope: ServerEnvelope<T>) => void): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    const wrapped: Listener = (p, e) => fn(p as T, e as ServerEnvelope<T>);
    set.add(wrapped);
    return () => {
      set!.delete(wrapped);
    };
  }

  /** Observe connection status changes (for a "reconnecting…" hint). */
  onStatus(fn: StatusListener): () => void {
    this.statusListeners.add(fn);
    return () => {
      this.statusListeners.delete(fn);
    };
  }

  /**
   * Send an action body. Queued until the socket is OPEN; queued messages are
   * flushed after the subscribe handshake on the next open, so a roll logged
   * during a blip is still delivered (and audited) rather than dropped.
   */
  send(body: object): void {
    const json = JSON.stringify(body);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(json);
    } else {
      this.pendingSends.push(json);
    }
  }

  /** Idempotent: a second start() while running shares the existing loop. */
  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;
    this.stopped = false;
    try {
      await this.connectLoop();
    } finally {
      this.running = false;
    }
  }

  stop(): void {
    this.stopped = true;
    this.setStatus('stopped');
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  isRunning(): boolean {
    return this.running;
  }

  private setStatus(next: SessionLiveStatus): void {
    if (this._status === next) return;
    this._status = next;
    for (const fn of [...this.statusListeners]) {
      try {
        fn(next);
      } catch (err) {
        console.warn('[SessionLive] status listener threw', err);
      }
    }
  }

  private async connectLoop(): Promise<void> {
    while (!this.stopped) {
      this.setStatus(this.everOpened ? 'reconnecting' : 'connecting');
      try {
        const ws = await openSocket();
        this.ws = ws;
        await this.waitForOpen(ws);
        this.backoffMs = 500;
        this.everOpened = true;
        ws.addEventListener('message', (ev) => this.onMessage(ev.data));
        // Bind the connection row to the session FIRST — anything queued
        // (e.g. a roll logged mid-blip) goes out after the subscribe so the
        // handler sees a session-bound connection.
        ws.send(
          JSON.stringify({
            action: SceneActions.Subscribe,
            sessionId: this.opts.sessionId,
            mapId: this.opts.mapId,
          }),
        );
        for (const msg of this.pendingSends) ws.send(msg);
        this.pendingSends = [];
        this.setStatus('open');
        await this.waitForClose(ws);
      } catch (err) {
        console.warn('[SessionLive] connect failed', err);
      }
      this.ws = null;
      if (this.stopped) break;
      this.setStatus('reconnecting');
      const jitter = Math.random() * 200;
      await new Promise((r) => setTimeout(r, this.backoffMs + jitter));
      this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
    }
  }

  private waitForOpen(ws: WebSocket): Promise<void> {
    if (ws.readyState === WebSocket.OPEN) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const onOpen = () => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        resolve();
      };
      const onError = (ev: Event) => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        reject(new Error(`WebSocket error before open: ${ev.type}`));
      };
      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
    });
  }

  private waitForClose(ws: WebSocket): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        ws.removeEventListener('close', done);
        ws.removeEventListener('error', done);
        resolve();
      };
      ws.addEventListener('close', done);
      ws.addEventListener('error', done);
    });
  }

  private onMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let env: ServerEnvelope<unknown>;
    try {
      env = JSON.parse(raw);
    } catch {
      return;
    }
    if (!env || typeof env.type !== 'string') return;
    const set = this.listeners.get(env.type);
    if (!set || set.size === 0) return;
    for (const fn of [...set]) {
      try {
        fn(env.payload, env);
      } catch (err) {
        console.warn('[SessionLive] listener threw', err);
      }
    }
  }
}

/**
 * Module-level singleton keyed on the session it was created for. The app
 * shell creates it once; a different session id (not a thing today — there's
 * one campaign) would replace the instance.
 */
let _singleton: SessionLive | null = null;
let _singletonKey = '';
export function getSessionLive(opts: SessionLiveOptions): SessionLive {
  const key = `${opts.sessionId}::${opts.mapId}`;
  if (!_singleton || _singletonKey !== key) {
    _singleton?.stop();
    _singleton = new SessionLive(opts);
    _singletonKey = key;
  }
  return _singleton;
}
